import React, { useRef, useEffect } from "react"
import { useBlocCoordinator } from "@lib/bloc/hooks"
import { DI } from "@ui/DI"
import { Button, ButtonProps, ButtonType } from "@ui/common/buttons/Button"
import { ServiceItem } from "@ui/workRequests/ServiceItem"
import { ServiceItemDeleteConfirmModal } from "@ui/workRequests/ServiceItemDeleteConfirmModal"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { ServiceItemDeclineModal } from "@ui/workRequests/ServiceItemDeclineModal"
import { AddExistingServiceItemsDialog } from "@ui/workRequests/AddExistingServiceItemsDialog"
import { Asset } from "@model/assets/Asset"
import { DismissWorkRequestModal } from "./DismissWorkRequestModal"
import { RemoveWorkRequestModal } from "./RemoveWorkRequestModal"
import { Grid } from "@ui/common/grids/Grid"
import { ServiceFormType } from "@model/serviceRequests/ServiceForm"
import { serviceItemFormToWorkRequest } from "@service/workRequests/MainWorkRequestService"

export function ServiceItemsList({ isEnabled = true, asset, SaveButton, isClosedWorkOrder = false }: { isEnabled: boolean, asset?: Asset | null, SaveButton: JSX.Element, isClosedWorkOrder?: boolean }): JSX.Element {
    const [state, viewModel] = useBlocCoordinator(() => DI.serviceItemsViewModel())

    const lastItemRef = useRef<HTMLDivElement | null>(null)
    const endOfListRef = useRef<HTMLDivElement | null>(null)
    const prevCount = useRef(0)
    const [itemToDelete, setItemToDelete] = React.useState<ServiceItemForm | null>(null)
    const [itemToDeclineId, setItemToDeclineId] = React.useState<number | null>(null)
    const [isAddExistingServiceItemsDialogOpen, setAddExistingServiceItemsDialogOpen] = React.useState(false)

    const handleFollowUpClicked = (item: ServiceItemForm) => {
        const workOrder = DI.createNewWorkOrderScreenBloc().state.workOrder
        if (!workOrder) return
        if (asset) DI.serviceRequestCreationBloc().selectedAssetChanged(asset)
        const workRequest = serviceItemFormToWorkRequest(item)
        DI.serviceRequestCreationBloc().showModal(ServiceFormType.Request, workRequest, workOrder)
    }

    const handleViewFollowUpClicked = (item: ServiceItemForm) => {
        if (!item.childFollowUpId || !asset?.id) return
        window.open(`/work-requests/asset/${asset.id}/${item.childFollowUpId}`, "_blank")
    }

    useEffect(() => {
        let timeout: NodeJS.Timeout | undefined

        if (state.items.length > prevCount.current) {

            endOfListRef.current?.scrollIntoView({ behavior: "smooth" })

            if (lastItemRef.current) {
                const el = lastItemRef.current
                el.classList.add("service-item-enter")
                void el.offsetWidth
                el.classList.add("service-item-enter-active")

                timeout = setTimeout(() => {
                    el.classList.remove("service-item-enter", "service-item-enter-active")
                }, 300)
            }
        }

        prevCount.current = state.items.length

        return () => {
            if (timeout) clearTimeout(timeout)
        }
    }, [state.items.length])

    const hasItems = state.items.length > 0

    return (
        <>
            <div className="service-items-container">
                <div className="service-items-header">
                    <div>
                        <div className="title">
                            Service Items <span className="count">{state.count}</span>
                        </div>
                        <div className="subtitle">Add individual services and tasks for this work order</div>
                    </div>

                    {hasItems && (
                        <div className="totals-box">
                            <div className="total-block">
                                Labor <strong>${state.totals.labor.toFixed(2)}</strong>
                            </div>
                            <div className="total-block">
                                Parts <strong>${state.totals.parts.toFixed(2)}</strong>
                            </div>
                            <div className="total-block">
                                Misc <strong>${state.totals.misc.toFixed(2)}</strong>
                            </div>
                            <div className="total-block grand-total">
                                Total <strong>${state.totals.total.toFixed(2)}</strong>
                            </div>
                        </div>
                    )}
                </div>

                {hasItems ? (
                    <>
                        {state.items.map((item, idx) => {
                            const itemViewModel = viewModel.getItemViewModel(item.uiElementId)
                            if (!itemViewModel) return null
                            const isLast = idx === state.items.length - 1
                            return (
                                <div key={item.uiElementId} ref={isLast ? lastItemRef : null}>
                                    <ServiceItem
                                        viewModel={itemViewModel}
                                        onRemoveClicked={() => {
                                            if (viewModel.isEditingAnExistingItemOnAWorkOrder(item)) {
                                                viewModel.dismissWorkRequestClicked(item)
                                            }
                                            else {
                                                setItemToDelete(item)
                                            }
                                        }}
                                        index={idx}
                                        onDeclineClicked={() => {
                                            if (viewModel.isEditingAnExistingItemOnAWorkOrder(item)) {
                                                viewModel.removeServiceItemClicked(item)
                                            }
                                        }
                                        }
                                        onCompleteClicked={() => { }}
                                        onFollowUpClicked={() => handleFollowUpClicked(item)}
                                        onViewFollowUpClicked={() => handleViewFollowUpClicked(item)}
                                        isEnabled={isEnabled}
                                        isClosedWorkOrder={isClosedWorkOrder}
                                    />
                                </div>
                            )
                        })}

                        <div ref={endOfListRef} />

                        <Grid columns={4} style={{gap: "1rem", marginTop: "2rem"}} >
                            <div></div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                label="Add New Service Item"
                                type={ButtonType.Contained}
                                isEnabled={state.items.every((i) => i.isSaved) && isEnabled && viewModel.isWorkOrderNotClosed()}
                                onClick={() => viewModel.addItem()}                                
                            />
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-start" }}>
                                <Button
                                label="Add Existing Service Items"
                                type={ButtonType.Outlined}
                                isEnabled={isEnabled && viewModel.isWorkOrderNotClosed()}
                                onClick={() => {
                                    const assetId = asset?.id
                                    if (!assetId) {
                                        console.warn("No asset selected, cannot load existing items")
                                    } else {
                                        setAddExistingServiceItemsDialogOpen(true)
                                    }
                                }}
                            />
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                {state.items.some((i) => i.isSaved) && <div>{SaveButton}</div>}
                            </div>
                        </Grid>
                    </>
                ) : (
                    <div className="service-items-empty">
                        <p className="empty-title">No services added yet</p>
                        <p className="empty-subtitle">
                            Add services to track individual tasks and labor for this work order
                        </p>
                        <div className="empty-actions">                            
                            <Button
                                label="Add New Service Item"
                                type={ButtonType.Contained}
                                onClick={() => viewModel.addItem()}
                                isEnabled={isEnabled}
                            />                            
                            <Button
                                label="Add Existing Service Items"
                                isEnabled={asset?.id !== undefined && isEnabled}
                                type={asset?.id !== undefined ? ButtonType.Outlined : ButtonType.Contained}
                                onClick={() => {
                                    const assetId = asset?.id
                                    if (!assetId) {
                                        console.warn("No asset selected, cannot load existing items")
                                    } else {
                                        setAddExistingServiceItemsDialogOpen(true)
                                    }
                                }}
                            />                            
                        </div>
                    </div>
                )}
            </div>
            {/* Reusing RemoveWorkRequestModal dialog from M2.0 */}
            <RemoveWorkRequestModal
                removeTerminology="Decline"
                removeVerbTerminology="Declining"
                itemDescriptionTerminology="Service Item"
                workRequest={state.workRequestToRemove}
                customMessage={"Are you sure you wish to decline this service item from this work order? Declining a service item will return it to the queue of service items."}
                onCancel={() => viewModel.removeWorkRequestCancelClicked()}
                onConfirm={(request) => viewModel.removeWorkRequestConfirmClicked(request)}
            />
            {/* Reusing DismissWorkRequestModal dialog from M2.0 */}
            <DismissWorkRequestModal
                dismissTerminology="Delete"
                dismissVerbTerminology="Deleting"
                itemDescriptionTerminology="Service Item"
                assetTag={asset?.tag ?? null}
                data={state.workRequestToDismiss}
                onCancel={() => viewModel.dismissWorkRequestCancelClicked()}
                onReasonChanged={(workRequest, reason) =>
                    viewModel.dismissWorkRequestReasonChanged(workRequest, reason)
                }
                onConfirm={() => viewModel.dismissWorkRequestConfirmClicked()}
                onTagChanged={(request, change) => viewModel.dismissWorkRequestTagChanged(request, change)}
                onTagReasonChanged={(workRequest, tagReason) =>
                    viewModel.dismissWorkRequestTagReasonChanged(workRequest, tagReason)
                }
            />
            {/* New ServiceItemDeleteConfirmModal dialog for M2.5 (deletes items that have not been saved yet. And its attachments if any) */}
            <ServiceItemDeleteConfirmModal
                serviceItem={itemToDelete}
                onCancel={() => setItemToDelete(null)}
                onConfirm={(item) => {
                    viewModel.deleteItem(item.uiElementId)
                    setItemToDelete(null)
                }}
            />
            <ServiceItemDeclineModal
                viewModel={itemToDeclineId ? viewModel.getItemViewModel(itemToDeclineId) ?? null : null}
                onCancel={() => setItemToDeclineId(null)}
                onConfirm={(id) => {
                    const vm = viewModel.getItemViewModel(id)
                    if (vm) {
                        viewModel.updateItem(vm.getForm())
                    }
                    setItemToDeclineId(null)
                }}
            />
            <AddExistingServiceItemsDialog
                isOpen={isAddExistingServiceItemsDialogOpen}
                assetId={asset?.id ?? null}
                onClose={() => setAddExistingServiceItemsDialogOpen(false)}
                alreadySelectedIds={state.items.map(i => i.id!).filter(id => id !== undefined) as number[]}
                onItemSelected={async (item) => {
                    await viewModel.addExistingItem(item)
                }}
            />
        </>
    )
}